// Comments Modal - Display comments with option to add new ones
import { db, auth } from '../firebase.js';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { CommentModal } from './commentModal.js';

export class CommentsModal {
  constructor(postId, postData) {
    this.postId = postId;
    this.postData = postData;
    this.unsubscribe = null;
  }

  open() {
    const modal = document.createElement('div');
    modal.className = 'comments-view-modal-overlay';
    modal.innerHTML = `
      <div class="comments-view-modal">
        <div class="modal-header">
          <h2>💬 Commentaires</h2>
          <button class="close-btn">✕</button>
        </div>
        
        <div class="comments-view-content">
          <div id="comments-list" class="comments-list">
            <div class="loading">Chargement des commentaires...</div>
          </div>
        </div>

        <div class="comments-view-footer">
          <button id="add-comment-btn" class="btn-primary">
            ✍️ Ajouter un commentaire
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    this.modalElement = modal;
    this.attachEventListeners();
    this.loadComments();
  }

  attachEventListeners() {
    const closeBtn = this.modalElement.querySelector('.close-btn');
    const addCommentBtn = this.modalElement.querySelector('#add-comment-btn');

    closeBtn.addEventListener('click', () => this.close());
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) this.close();
    });

    addCommentBtn.addEventListener('click', () => {
      this.openAddCommentModal();
    });
  }

  loadComments() {
    const commentsQuery = query(
      collection(db, 'posts', this.postId, 'comments'),
      orderBy('createdAt', 'desc')
    );

    const listContainer = this.modalElement.querySelector('#comments-list');

    this.unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
      listContainer.innerHTML = '';

      if (snapshot.empty) {
        listContainer.innerHTML = '<div class="no-comments">Aucun commentaire pour le moment</div>';
        return;
      }

      snapshot.docs.forEach(docSnap => {
        const comment = docSnap.data();
        const commentEl = this.createCommentElement(docSnap.id, comment);
        listContainer.appendChild(commentEl);
      });
    }, (error) => {
      console.error('Erreur chargement commentaires:', error);
      listContainer.innerHTML = '<div class="no-comments">Erreur de chargement</div>';
    });
  }

  createCommentElement(commentId, comment) {
    const commentEl = document.createElement('div');
    commentEl.className = 'comment-item-view';
    commentEl.dataset.commentId = commentId;

    const currentUser = auth.currentUser;
    const isAuthor = currentUser && currentUser.uid === comment.authorId;

    const timeStr = comment.createdAt ? 
      new Date(comment.createdAt.toDate()).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      }) : 'Il y a un instant';

    let linkedVersesHTML = '';
    if (comment.linkedVerses && comment.linkedVerses.length > 0) {
      linkedVersesHTML = `
        <div class="comment-linked-verses">
          ${comment.linkedVerses.map(v => `
            <span class="verse-badge">
              📖 ${v.bookName || v.book} ${v.chapter}:${v.verseStart}${v.verseStart !== v.verseEnd ? '-' + v.verseEnd : ''}
            </span>
          `).join('')}
        </div>
      `;
    }

    commentEl.innerHTML = `
      <div class="comment-header">
        <img src="${comment.photoURL || '/default-avatar.png'}" alt="Avatar" class="comment-avatar">
        <div class="comment-info">
          <strong>${comment.displayName || 'Anonyme'}</strong>
          <span class="comment-time">${timeStr}</span>
        </div>
        ${isAuthor ? `
          <div class="comment-actions">
            <button class="comment-edit-btn" data-action="edit" title="Modifier">✏️</button>
            <button class="comment-delete-btn" data-action="delete" title="Supprimer">🗑️</button>
          </div>
        ` : ''}
      </div>
      <div class="comment-body">
        <p>${this.escapeHtml(comment.text)}</p>
        ${linkedVersesHTML}
      </div>
    `;

    if (isAuthor) {
      const editBtn = commentEl.querySelector('[data-action="edit"]');
      const deleteBtn = commentEl.querySelector('[data-action="delete"]');

      editBtn.addEventListener('click', () => this.editComment(commentId, comment));
      deleteBtn.addEventListener('click', () => this.deleteComment(commentId));
    }

    return commentEl;
  }

  openAddCommentModal() {
    // Hide this modal when opening the add comment modal
    this.close();
    
    const addModal = new CommentModal(this.postId, async (commentData) => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          alert('Vous devez être connecté pour commenter');
          return;
        }

        const { addDoc, collection, serverTimestamp, updateDoc, doc, getDoc } = await import('firebase/firestore');

        const comment = {
          text: commentData.text,
          linkedVerses: commentData.linkedVerses || [],
          authorId: currentUser.uid,
          displayName: currentUser.displayName || 'Anonyme',
          photoURL: currentUser.photoURL || '',
          createdAt: serverTimestamp(),
        };

        await addDoc(collection(db, 'posts', this.postId, 'comments'), comment);

        // Increment comments count
        const postRef = doc(db, 'posts', this.postId);
        const postSnap = await getDoc(postRef);
        if (postSnap.exists()) {
          await updateDoc(postRef, {
            commentsCount: (postSnap.data().commentsCount || 0) + 1,
          });
        }

        console.log('✅ Commentaire publié');
      } catch (error) {
        console.error('Error publishing comment:', error);
        alert('Erreur lors de la publication du commentaire');
      }
    });
    addModal.open();
  }

  async editComment(commentId, comment) {
    // Hide this modal when opening the edit comment modal
    this.close();
    
    const editModal = new CommentModal(this.postId, async (commentData) => {
      try {
        const commentRef = doc(db, 'posts', this.postId, 'comments', commentId);
        await updateDoc(commentRef, {
          text: commentData.text.trim(),
          linkedVerses: commentData.linkedVerses || [],
          updatedAt: new Date()
        });
        console.log('✅ Commentaire modifié');
      } catch (error) {
        console.error('Erreur modification commentaire:', error);
        alert('Impossible de modifier le commentaire');
      }
    }, comment); // Pass existing comment for editing
    
    editModal.open();
  }

  async deleteComment(commentId) {
    if (!confirm('Voulez-vous vraiment supprimer ce commentaire ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'posts', this.postId, 'comments', commentId));

      // Decrement comments count
      const postRef = doc(db, 'posts', this.postId);
      const { getDoc, updateDoc } = await import('firebase/firestore');
      const postSnap = await getDoc(postRef);
      if (postSnap.exists()) {
        await updateDoc(postRef, {
          commentsCount: Math.max(0, (postSnap.data().commentsCount || 1) - 1),
        });
      }

      console.log('✅ Commentaire supprimé');
    } catch (error) {
      console.error('Erreur suppression commentaire:', error);
      alert('Impossible de supprimer le commentaire');
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/\n/g, '<br>');
  }

  close() {
    if (this.unsubscribe) this.unsubscribe();
    this.modalElement.remove();
  }
}
